import { useNavigate } from '@tanstack/react-router';

import { DataPrivacyStatement } from '@/components/DataPrivacyStatement';
import { NavigationBar } from '@/components/NavigationBar.tsx';
import StepperCard from '@/components/common/StepperCard.tsx';
import { CardContent, CardHeader, CardTitle } from '@/components/ui/card.tsx';
import { useSessionSave } from '@/hooks/use-session-save.ts';
import { useSessionStore } from '@/hooks/use-session-store/use-session-store.ts';
import { isEligibleZipCode } from '@/types/insurance-builder.type.ts';

import {
  LegacyPatientInformationForm,
  type LegacyPatientInformationFormData,
} from './components/LegacyPatientInformationForm.tsx';
import {
  PatientInformationForm,
  type PatientInformationFormData,
} from './components/PatientInformationForm.tsx';

export function PatientInformation() {
  const navigate = useNavigate();
  const { saveSession } = useSessionSave();
  const isEligibilityRevamp = useSessionStore(
    (state) => state.wizardVariantId === 'eligibility-revamp'
  );

  const onLegacySubmit = (data: LegacyPatientInformationFormData) => {
    const { updateState } = useSessionStore.getState();

    updateState({
      patientRecipient: data.recipient,
      patientFirstName: data.firstName,
      patientLastName: data.lastName,
      patientDateOfBirth: data.dateOfBirth,
      patientGender: data.gender,
    });

    saveSession();
    navigate({ to: '/insurance/pre-discovery' });
  };

  const onRevampSubmit = (data: PatientInformationFormData) => {
    const { updateState } = useSessionStore.getState();

    updateState({
      patientRecipient: data.recipient,
      patientFirstName: data.firstName,
      patientLastName: data.lastName,
      patientDateOfBirth: data.dateOfBirth,
      patientGender: data.gender,
      insuranceZipCode: data.zipCode,
    });

    saveSession();

    if (!isEligibleZipCode(data.zipCode)) {
      navigate({ to: '/insurance/ineligible', search: { reason: 'zip_fail' } });
      return;
    }

    navigate({ to: '/insurance/discovery-pending' });
  };

  const handleBack = () => {
    navigate({ to: '/0' });
  };

  return (
    <section>
      <NavigationBar
        onBack={handleBack}
        backLabel="Categories"
        stepIndicator={isEligibilityRevamp ? '1 of 3' : '1 of 4'}
      />
      <StepperCard currentStep={1} totalSteps={isEligibilityRevamp ? 3 : 4}>
        <CardHeader>
          <CardTitle>Let's check eligibility to confirm coverage for supplies.</CardTitle>
        </CardHeader>
        <CardContent>
          {isEligibilityRevamp ? (
            <PatientInformationForm onSubmit={onRevampSubmit} />
          ) : (
            <LegacyPatientInformationForm onSubmit={onLegacySubmit} />
          )}
        </CardContent>
      </StepperCard>

      <DataPrivacyStatement />
    </section>
  );
}
